import React from "react";
import {
  Typography,
  IconButton,
  Tooltip,
  Badge,
  TextField
} from "@mui/material";
import AdapterDateFns from "@mui/lab/AdapterDateFns";
import LocalizationProvider from "@mui/lab/LocalizationProvider";
import DatePicker from "@mui/lab/DatePicker";
import itLocale from "date-fns/locale/it";

import EmailIcon from "@mui/icons-material/Email";
import PeopleIcon from "@mui/icons-material/People";
import SyncIcon from "@mui/icons-material/Sync";
import PersonAddIcon from "@mui/icons-material/PersonAdd";
import HowToRegIcon from "@mui/icons-material/HowToReg";
import EditIcon from "@mui/icons-material/Edit";
import EditOffIcon from "@mui/icons-material/EditOff";
import VisibilityIcon from "@mui/icons-material/Visibility";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import LocalPrintshopIcon from "@mui/icons-material/LocalPrintshop";
import ManageAccountsIcon from "@mui/icons-material/ManageAccounts";
import CalendarButton from "../../../../components/common/CalendarButton";

import HelperCalendarXls from "./helperCalendarXls";

function CalendarToolbar({
  calData,
  isMobile,
  editor,
  eventCalendarList,
  calendarSel,
  isEditEnabled,
  isPublished,
  hasDateExpired,
  vacantRequests,
  myPendingVacantRequests,
  isVacantSelectMode,
  pendingShiftRequestsCount,
  defaultDate,
  maxDate,
  t,
  onToggleEdit,
  onChangeShiftWithOtherUser,
  onToggleVacantMode,
  onOpenShiftRequests,
  onRefreshCalendar,
  onChangeShiftStatus,
  onToggleSidePanel,
  openSidePanel,
  onPrintOnFile,
  onMonthChange
}) {
  const isAdminOrManager = editor;
  const badgeCount = isAdminOrManager
    ? vacantRequests
      ? vacantRequests.filter((r) => !r.confirmedAt && !r.refusedAt).length
      : 0
    : myPendingVacantRequests.length;

  const tooltipTitle = isAdminOrManager
    ? t("Gestisci Richieste Turni Vacanti")
    : isVacantSelectMode
    ? t("Disattiva selezione turni vacanti")
    : t("Richiesta Turno Vacante");

  const IconComponent = isAdminOrManager ? HowToRegIcon : PersonAddIcon;
  const isActiveVolunteerMode = !isAdminOrManager && isVacantSelectMode;

  return (
    <>
      <div className="flex-row-cal">
        <Typography variant="h6" component="h2">
          {calData && calData.name}{" "}
        </Typography>
        <HelperCalendarXls
          isMobile={isMobile}
          isAdmin={editor}
          isManager={editor}
          hasEventList={eventCalendarList.length > 0}
        />
      </div>

      <div id="calx_title" className="flex-row-cal">
        {calendarSel && isEditEnabled && (
          <small>
            ({t("calendarAct")}{" "}
            {isPublished === 0 ? t("calendarPubS0") : t("calendarPubA0")})
          </small>
        )}

        {calendarSel && isPublished !== null && !isEditEnabled && (
          <>
            {isPublished === 1 && (
              <>
                {!hasDateExpired() && (
                  <div style={{ padding: "2px" }}>
                    <IconButton
                      id="calx_change"
                      color="primary"
                      title={t("calendarChaTool")}
                      onClick={onChangeShiftWithOtherUser}
                    >
                      <PeopleIcon />
                    </IconButton>
                  </div>
                )}

                <Tooltip title={tooltipTitle}>
                  <IconButton
                    id="calx_vacant"
                    color={isActiveVolunteerMode ? "primary" : "default"}
                    onClick={onToggleVacantMode}
                    sx={{
                      bgcolor: isActiveVolunteerMode
                        ? "rgba(3, 120, 213, 0.12)"
                        : "transparent",
                      border: isActiveVolunteerMode
                        ? "2px solid #0378d5"
                        : "none",
                    }}
                  >
                    <Badge badgeContent={badgeCount} color="error">
                      <IconComponent />
                    </Badge>
                  </IconButton>
                </Tooltip>

                <div style={{ padding: "2px" }}>
                   <Tooltip 
                      title={`${pendingShiftRequestsCount.in} da approvare, ${pendingShiftRequestsCount.out} in attesa`}
                    >
                      <IconButton
                        id="calx_pending"
                        color="primary"
                        title={t("pendingShiftRequests")}
                        onClick={onOpenShiftRequests}
                      >
                        <Badge badgeContent={pendingShiftRequestsCount.tot} color="error">
                          <EmailIcon />
                        </Badge>
                      </IconButton>
                    </Tooltip>
                </div>

                <div style={{ padding: "2px" }}>
                  <IconButton
                    id="calx_refresh"
                    color="primary"
                    title={t("refreshCalendar")}
                    onClick={() => onRefreshCalendar()}
                  >
                    <SyncIcon />
                  </IconButton>
                </div>
                {eventCalendarList.length > 0 && (
                  <div style={{ padding: "2px" }}>
                    <CalendarButton
                      data={eventCalendarList}
                      filename={`calendario_${calendarSel?.year}_${calendarSel?.month}.ics`}
                      t={t}
                      buttonProps={{
                        id: "calx-add-to-cal",
                        sx: {
                          color: "#2C5282",
                          "&:hover": {
                            backgroundColor: "#2C5282",
                            color: "#fff",
                          },
                        }
                      }}
                    />
                  </div>
                )}
              </>
            )}
          </>
        )}

        {calendarSel && isPublished !== null && isEditEnabled && (
          <>
            {isPublished === 0 && (
              <div style={{ padding: "2px" }}>
                <IconButton
                  id="calx-publish"
                  color="success"
                  title={t("calendarPubTool")}
                  onClick={() => onChangeShiftStatus(1)}
                >
                  <VisibilityIcon />
                </IconButton>
              </div>
            )}
            {isPublished === 1 && (
              <div style={{ padding: "2px" }}>
                <IconButton
                  id="calx-hide"
                  color="error"
                  title={t("calendarSusTool")}
                  onClick={() => onChangeShiftStatus(0)}
                >
                  <VisibilityOffIcon />
                </IconButton>
              </div>
            )}
          </>
        )}

        {calendarSel && isEditEnabled && (
          <>
            <div style={{ padding: "2px" }}>
              <IconButton
                id="calx-assign-shift"
                color="primary"
                title={t("calendarUserList")}
                onClick={onToggleSidePanel}
              >
                <ManageAccountsIcon />
              </IconButton>
            </div>
            <div style={{ padding: "2px" }}>
              <IconButton
                id="calx-print"
                color="primary"
                title={t("calendarPrint")}
                onClick={onPrintOnFile}
              >
                <LocalPrintshopIcon />
              </IconButton>
            </div>
          </>
        )}

        {calendarSel && editor && (
          <div style={{ padding: "5px" }} className="headerLine">
            <IconButton
              id="calx-toggle-edit"
              sx={{ color: "#FB8C00" }}
              title={
                isEditEnabled
                  ? t("calendarEditorR")
                  : t("calendarEditorW")
              }
              onClick={onToggleEdit}
            >
              {!isEditEnabled ? <EditIcon /> : <EditOffIcon />}
            </IconButton>
          </div>
        )}

        <div
          id="calx_datepicker"
          className="flex-data-picker-cal"
          style={{ padding: "5px" }}
        >
          <LocalizationProvider
            dateAdapter={AdapterDateFns}
            locale={itLocale}
          >
            <DatePicker
              views={["year", "month"]}
              minDate={new Date("2022-01-01")}
              maxDate={maxDate}
              value={defaultDate}
              onChange={onMonthChange}
              renderInput={(params) => (
                <TextField {...params} 
                  helperText={null} 
                  size="small"
                  sx={{
                    width: 'fit-content',  // 👈 Si adatta al contenuto
                    minWidth: '120px',     // 👈 Larghezza minima
                    '& .MuiInputBase-input': {
                      fontSize: '0.9rem',  // 👈 Carattere più piccolo
                      py: 1,             // 👈 Padding verticale minimo
                      px: 1,               // 👈 Padding orizzontale minimo
                      with: "auto"
                    },
                    '& .MuiInputBase-root': {
                      height: '36px',      // 👈 Altezza fissa compatta
                    }
                  }}/>
              )}

            />
          </LocalizationProvider>
        </div>
      </div>
    </>
  );
}

export default React.memo(CalendarToolbar);